import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { Phone, User, Calendar, Gift, CheckCircle, AlertCircle, ArrowRight, Building2 } from 'lucide-react';

function ClinicWelcome() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const hospitalId = params.get('h');
    const patientUid = params.get('p');

    const [hospital, setHospital] = useState(null);
    const [patient, setPatient] = useState(null);
    const [callTime, setCallTime] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (u) => setUser(u));
        return () => unsubAuth();
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                if (hospitalId) {
                    const hospitalSnap = await getDoc(doc(db, 'hospitals', hospitalId));
                    if (hospitalSnap.exists()) {
                        const data = hospitalSnap.data();
                        setHospital({ id: hospitalSnap.id, ...data });
                        if (data.lastCallAt) setCallTime(data.lastCallAt.toDate());
                    }
                }
                if (patientUid) {
                    const patientSnap = await getDoc(doc(db, 'users', patientUid));
                    if (patientSnap.exists()) {
                        const data = patientSnap.data();
                        const phone = (data.phone || '').replace(/[^0-9]/g, '');
                        setPatient({
                            uid: patientUid,
                            name: data.name || '환자',
                            phoneMasked: phone.length >= 4 ? `***-****-${phone.slice(-4)}` : '',
                            birthYear: data.birthYear,
                        });
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [hospitalId, patientUid]);

    if (loading) {
        return <div style={S.center}><div style={S.spinner} /></div>;
    }
    if (!hospital) {
        return (
            <div style={S.container}>
                <div style={S.errorBox}>
                    <AlertCircle size={20} />
                    <p>치과 정보를 찾을 수 없습니다. URL을 확인해주세요.</p>
                </div>
            </div>
        );
    }

    const isClaimed = !!hospital.claimedBy;
    const claimedByMe = user && hospital.claimedBy === user.uid;

    return (
        <div style={S.container}>
            <div style={S.hero}>
                <div style={S.heroEmoji}>📞</div>
                <h1 style={S.heroTitle}>방금 환자 1명이<br /><span style={{ color: '#fef3c7' }}>{hospital.name}</span>으로 전화했어요</h1>
                {patient && (
                    <p style={S.heroSubtitle}>
                        {patient.name}님 ({patient.phoneMasked})
                        {patient.birthYear && ` · ${new Date().getFullYear() - patient.birthYear}세`}
                    </p>
                )}
                {callTime && (
                    <p style={S.heroTime}>{formatTime(callTime)} 환자앱에서 검색·전화</p>
                )}
            </div>

            <div style={S.benefitsCard}>
                <h2 style={S.benefitsTitle}><Gift size={20} /> 첫 환자는 무료입니다</h2>
                <p style={S.benefitsDesc}>
                    덴탈클러스터 환자앱에 가입하면 <strong>이 환자에게 영수증·X-ray·진료기록을
                    스마트폰으로 자동 전송</strong>할 수 있어요.
                </p>
                <ul style={S.benefitsList}>
                    <li>✅ 첫 환자 1명까지 100% 무료</li>
                    <li>✅ 의료기록 자동 전송 (직원 시간 5분 → 30초)</li>
                    <li>✅ 환자 자동 검진 리마인더 (재진율 ↑)</li>
                    <li>✅ 환자 예약 자동 접수</li>
                    <li>✅ 환자 만족도 분석 대시보드</li>
                    <li>✅ 두 번째 환자부터 월 20만원 (카드결제 자동)</li>
                </ul>
            </div>

            {claimedByMe ? (
                <div style={S.successCard}>
                    <CheckCircle size={20} color="#10b981" />
                    <div>
                        <strong>이미 등록된 치과입니다</strong>
                        <p style={{ marginTop: 4, fontSize: 13, color: '#475569' }}>
                            지금 바로 환자에게 의료기록을 전송할 수 있어요.
                        </p>
                    </div>
                    {/* 구 환자앱 폐기 09-14 — 의료기록 전송 CTA 숨김 (라우트는 /patients·/clinic-today로 리다이렉트). 폐기 계획 승인 후 제거
                    <button onClick={() => navigate(`/clinic/send-record?p=${patientUid || ''}`)} style={S.ctaBtn}>
                        의료기록 전송 <ArrowRight size={16} />
                    </button>
                    */}
                </div>
            ) : isClaimed ? (
                <div style={S.errorBox}>
                    <AlertCircle size={20} />
                    <p>이미 다른 사용자가 이 치과를 등록했습니다.</p>
                </div>
            ) : (
                <div style={S.ctaCard}>
                    <h3 style={S.ctaTitle}>{user ? '본인 치과로 등록' : '먼저 회원가입 후 등록'}</h3>
                    <p style={S.ctaDesc}>
                        {hospital.name}<br />
                        {hospital.address}<br />
                        {hospital.phone}
                    </p>
                    {user ? (
                        <button onClick={() => navigate('/dashboard')} style={S.ctaBtn}>
                            <Building2 size={18} /> 대시보드로 이동 <ArrowRight size={16} />
                        </button>
                    ) : (
                        <>
                            <button onClick={() => navigate('/signup?from=clinic-welcome&h=' + (hospitalId || ''))} style={S.ctaBtn}>
                                회원가입 → 치과 등록 <ArrowRight size={16} />
                            </button>
                            <button onClick={() => navigate('/login?from=clinic-welcome&h=' + (hospitalId || ''))} style={{ ...S.ctaBtn, background: 'transparent', color: '#6366f1', border: '1px solid #6366f1' }}>
                                이미 회원이신가요? 로그인
                            </button>
                        </>
                    )}
                </div>
            )}

            <div style={S.legalNote}>
                ※ 본 서비스는 치과 운영 자동화 도구입니다. 환자 검색·전화 노출은 전국 모든 치과에 동일하게 제공되며,
                구독료는 의료기록 자동 전송·예약 시스템·분석 등 SaaS 도구 사용료입니다.
                의료법 제27조(환자 유인·알선) 및 제56조(의료광고)를 준수합니다.
            </div>
        </div>
    );
}

function formatTime(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const S = {
    container: { maxWidth: 600, margin: '0 auto', padding: 20, fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    hero: { background: 'linear-gradient(135deg, #6366f1, #06b6d4)', color: '#fff', padding: '40px 24px', borderRadius: 20, textAlign: 'center', marginBottom: 16 },
    heroEmoji: { fontSize: 48, marginBottom: 8 },
    heroTitle: { fontSize: 22, fontWeight: 900, lineHeight: 1.4, margin: 0 },
    heroSubtitle: { fontSize: 14, marginTop: 16, opacity: 0.95 },
    heroTime: { fontSize: 12, marginTop: 4, opacity: 0.8 },
    benefitsCard: { background: '#fff', border: '2px solid #fbbf24', borderRadius: 14, padding: 20, marginBottom: 16 },
    benefitsTitle: { fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0, color: '#92400e' },
    benefitsDesc: { fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 12 },
    benefitsList: { paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#0f172a', lineHeight: 1.6 },
    ctaCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20, marginBottom: 16 },
    ctaTitle: { fontSize: 16, fontWeight: 800, marginTop: 0 },
    ctaDesc: { fontSize: 13, color: '#64748b', lineHeight: 1.7, marginBottom: 16 },
    ctaBtn: { width: '100%', padding: '14px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 },
    successCard: { background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10 },
    errorBox: { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12, padding: 16, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 10 },
    legalNote: { fontSize: 11, color: '#94a3b8', lineHeight: 1.7, padding: 12, background: '#f8fafc', borderRadius: 8, marginTop: 16 },
    center: { padding: 80, textAlign: 'center' },
    spinner: { width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' },
};

export default ClinicWelcome;
